import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const WildlifeAreas = () => {
  const [selectedState, setSelectedState] = useState('VA');
  const [accessType, setAccessType] = useState('All');
  const [huntingType, setHuntingType] = useState('');
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const states = [
    { code: 'VA', name: 'Virginia' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'GA', name: 'Georgia' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'NY', name: 'New York' },
    { code: 'TX', name: 'Texas' }
  ];

  const accessTypes = [
    { value: 'All', label: 'All Access Types' },
    { value: 'public', label: 'Public Land' },
    { value: 'private', label: 'Private Land' },
    { value: 'lease', label: 'Lease Available' },
    { value: 'permission', label: 'Permission Required' }
  ];

  const huntingTypes = [
    'Deer', 'Turkey', 'Waterfowl', 'Small Game', 
    'Upland Birds', 'Bear', 'Elk', 'Predators'
  ];

  useEffect(() => {
    fetchWildlifeAreas();
  }, [selectedState, accessType, huntingType]);

  const fetchWildlifeAreas = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/wildlife-areas?state_code=${selectedState}`;
      if (accessType && accessType !== 'All') {
        url += `&access_type=${accessType}`;
      }
      if (huntingType) {
        url += `&hunting_type=${huntingType}`;
      }
      
      const response = await axios.get(url);
      setAreas(response.data.items || []);
    } catch (error) {
      console.error('Error fetching wildlife areas:', error);
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAreas = areas.filter(area =>
    area.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (area.county && area.county.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getAccessTypeBadge = (type) => {
    const badges = {
      public: 'bg-green-100 text-green-800',
      private: 'bg-blue-100 text-blue-800',
      lease: 'bg-purple-100 text-purple-800',
      permission: 'bg-yellow-100 text-yellow-800'
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-green-700 to-green-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold mb-4">Wildlife Management Areas</h1>
          <p className="text-green-100 text-lg">
            Discover public and private hunting grounds across your state
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {states.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Type
              </label>
              <select
                value={accessType}
                onChange={(e) => setAccessType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {accessTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hunting Type
              </label>
              <select
                value={huntingType}
                onChange={(e) => setHuntingType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                {huntingTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search by name or county..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
            <p className="mt-4 text-gray-600">Loading wildlife areas...</p>
          </div>
        ) : filteredAreas.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-yellow-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Wildlife areas data coming soon!</h3>
            <p className="text-gray-600 mb-4">
              We're currently researching comprehensive wildlife management areas for {states.find(s => s.code === selectedState)?.name}.
            </p>
            <p className="text-sm text-gray-500">
              This will include public lands, private access opportunities, and lease information.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAreas.map((area) => (
              <div key={area.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex-1">
                      {area.name}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getAccessTypeBadge(area.access_type)}`}>
                      {area.access_type}
                    </span>
                  </div>

                  {area.county && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">County:</span> {area.county}
                    </p>
                  )}

                  {area.acreage && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Acreage:</span> {area.acreage.toLocaleString()} acres
                    </p>
                  )}
                  
                  {/* GPS Link */}
                  {area.gps_lat && area.gps_lng && (
                    <p className="text-sm mb-2">
                      <a 
                        href={`https://maps.google.com/?q=${area.gps_lat},${area.gps_lng}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        📍 Get Directions
                      </a>
                    </p>
                  )}
                  
                  {/* Facilities & Activities */}
                  {(area.primitive_camping || area.boat_ramps || area.shooting_ranges || area.hiking || area.birding || area.fishing) && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Facilities & Activities:</p>
                      <div className="flex flex-wrap gap-1">
                        {area.primitive_camping && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs" title="Primitive Camping">
                            🏕️ Camping
                          </span>
                        )}
                        {area.boat_ramps && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs" title="Boat Ramps">
                            🚤 Boat Ramp
                          </span>
                        )}
                        {area.shooting_ranges && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs" title="Shooting Range">
                            🎯 Range
                          </span>
                        )}
                        {area.hiking && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs" title="Hiking Trails">
                            🥾 Trails
                          </span>
                        )}
                        {area.birding && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs" title="Birding">
                            🦅 Birding
                          </span>
                        )}
                        {area.fishing && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-50 text-cyan-700 rounded text-xs" 
                                title={`${area.fishing === 'both' ? 'Trout & Warmwater' : area.fishing === 'trout' ? 'Trout' : 'Warmwater'} Fishing`}>
                            🎣 {area.fishing === 'both' ? 'Fishing (All)' : area.fishing === 'trout' ? 'Trout' : 'Fishing'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {area.hunting_types && area.hunting_types.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Available Hunting:</p>
                      <div className="flex flex-wrap gap-1">
                        {area.hunting_types.map((type, idx) => (
                          <span key={idx} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {area.address && (
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Address:</span> {area.address}
                    </p>
                  )}

                  {(area.contact_name || area.contact_phone || area.contact_email) && (
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">Contact Information:</p>
                      {area.contact_name && (
                        <p className="text-sm text-gray-600">{area.contact_name}</p>
                      )}
                      {area.contact_phone && (
                        <p className="text-sm text-gray-600">
                          <a href={`tel:${area.contact_phone}`} className="text-green-600 hover:text-green-700">
                            {area.contact_phone}
                          </a>
                        </p>
                      )}
                      {area.contact_email && (
                        <p className="text-sm text-gray-600">
                          <a href={`mailto:${area.contact_email}`} className="text-green-600 hover:text-green-700">
                            {area.contact_email}
                          </a>
                        </p>
                      )}
                    </div>
                  )}

                  {(area.permit_required || area.reservation_required) && (
                    <div className="mt-3 flex gap-2">
                      {area.permit_required && (
                        <span className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded">
                          Permit Required
                        </span>
                      )}
                      {area.reservation_required && (
                        <span className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded">
                          Reservation Required
                        </span>
                      )}
                    </div>
                  )}

                  {area.notes && (
                    <p className="mt-3 text-sm text-gray-500 italic border-t border-gray-100 pt-3">
                      {area.notes}
                    </p>
                  )}

                  {area.url && (
                    <a
                      href={area.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-block text-green-600 hover:text-green-700 text-sm font-medium"
                    >
                      Learn More →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredAreas.length > 0 && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Showing {filteredAreas.length} wildlife area{filteredAreas.length !== 1 ? 's' : ''}</strong> in {states.find(s => s.code === selectedState)?.name}
              {accessType !== 'All' && ` (${accessType} access)`}
              {huntingType && ` for ${huntingType} hunting`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WildlifeAreas;
